import React, { useEffect, useState } from 'react';
import { LoadingAnimatedInline } from '../components/Loading';
import { useAuth } from '../auth/AuthContext';

type UserRecord = {
  id: string;
  email: string;
  username: string;
  clientName: string;
  registrationDate: string;
  lastVisitDate: string;
  status: string;
  roles: string[];
};

const SearchUsers: React.FC<{
  onEditUser?: (userData: { id: string; email: string }) => void;
  onActionsUser?: (userData: { id: string, email: string}) => void;
}> = ({ onEditUser, onActionsUser }) => {
  const { user } = useAuth();

  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const executeSearch = async(query: string) => {
    setError(null);
    setHasSearched(true);
    setLoading(true);
    try {
      if(!user) throw new Error('Not authenticated')
      const token = await user.getIdToken();

      const res = await fetch('/api/admin/users/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({query})
      })

      if(!res.ok) throw new Error('Search failed')
      const data = await res.json();
      setResults(data.users || []);
    } catch(e) {
      console.error('Error searching users:', e);
      setError(e instanceof Error ? e.message : 'Failed to search users');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    executeSearch('@');
  }, [])

  const handleSearch = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    executeSearch(searchQuery.trim() || '@');
  };
  

  return (
    <div className="p-6 h-full flex flex-col">
      <h2 className="text-2xl font-bold mb-6">Search Users</h2>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex gap-2">
          <input
            type="search"
            placeholder="Search by email"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 border border-light-border p-2 rounded-md"
          />
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-light-main text-white rounded-md hover:bg-light-main-hover transition-colors duration-200 ease-in-out font-medium disabled:opacity-50"
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </form>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* Results */}
      {hasSearched && (
        <>
          {loading ? (
            <LoadingAnimatedInline />
          ) : results.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No users found</p>
          ) : (
            <div className="flex-1 overflow-y-auto overflow-x-auto border border-light-border rounded-md">
              <table className="w-full border-collapse">
                <thead className="bg-light-main text-light-text-alt sticky top-0">
                  <tr>
                    <th className="border-b border-light-border px-4 py-3 text-left font-bold whitespace-nowrap">Email</th>
                    <th className="border-b border-light-border px-4 py-3 text-left font-bold whitespace-nowrap">Username</th>
                    <th className="border-b border-light-border px-4 py-3 text-left font-bold whitespace-nowrap">Client Name</th>
                    <th className="border-b border-light-border px-4 py-3 text-left font-bold whitespace-nowrap">Registration Date</th>
                    <th className="border-b border-light-border px-4 py-3 text-left font-bold whitespace-nowrap">Last Visit Date</th>
                    <th className="border-b border-light-border px-4 py-3 text-left font-bold whitespace-nowrap">Status</th>
                    <th className="border-b border-light-border px-4 py-3 text-left font-bold whitespace-nowrap">Roles</th>
                    <th className="border-b border-light-border px-4 py-3 text-left font-bold whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((userRecord) => (
                    <tr key={userRecord.id} className="hover:bg-gray-50 border-b border-light-border">
                      <td className="px-4 py-3 text-sm font-medium truncate">{userRecord.email}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 truncate">{userRecord.username}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 truncate">{userRecord.clientName}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{userRecord.registrationDate}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{userRecord.lastVisitDate}</td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold whitespace-nowrap inline-block ${
                            userRecord.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : userRecord.status === 'inactive'
                                ? 'bg-gray-100 text-gray-800'
                                : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {userRecord.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 truncate">
                        {userRecord.roles.length > 0 ? userRecord.roles.join(', ') : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm gap-2 flex">
                        <button
                          onClick={() =>
                            onEditUser?.({
                              id: userRecord.id,
                              email: userRecord.email
                            })
                          }
                          className="px-3 py-1 bg-light-main text-white rounded text-xs font-semibold hover:bg-light-main-hover transition-colors"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => onActionsUser?.({
                            id: userRecord.id,
                            email: userRecord.email
                          })}
                          className="px-3 py-1 bg-light-main text-white rounded text-xs font-semibold hover:bg-light-main-hover transition-colors"
                          >
                          Actions
                        </button>
                        <button 
                          // onClick={() => onRemoveUser?.({
                          //   id: userRecord.id,
                          //   email: userRecord.email
                          // })}
                          className="px-3 py-1 bg-red-500 text-white rounded text-xs font-semibold hover:bg-red-800 transition-colors">
                            Remove
                          </button>
                      </td>
                      
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* No search yet */}
      {!hasSearched && (
        <div className="text-center text-gray-500 py-12">
          <p>Enter an email to search for users</p>
        </div>
      )}
    </div>
  );
};

export default SearchUsers;
